import {identity} from '@packages/utilities/fp/utils';
import last from 'lodash/last';
import {weakMemo, cacheMemo} from '@packages/utilities/memo';

import type {EnvironmentContextType} from '@packages/systems/wfdl';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  getActiveDocument,
  getActiveIframe,
} from '@packages/systems/core/helpers/SiteIframeMigrationHelper';
import SharedConfig from '@packages/systems/core/constants/SharedConfig';
import {shouldUseCodeIslandHostForMeasurement} from '@packages/systems/code-libraries/inner-node-utils';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {
  type SeenComponents,
  lastSeenComponentKind,
} from '@packages/systems/core/utils/SeenComponentsUtils';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/*
  Node Native Id Utilities

  This file should contain all utilities required to create and parse
  Node Native Ids.

  A Native Id is simply the Abstract Node Id with an Instance Suffix. It
  represents a unique identifier for the HTML element(s) rendered from
  the abstract node.

  Example Abstract Node Id: "000000000000000000000000000000000000"

  Example Instance Suffix: "_instance-2-1"

  Example Native Id: "000000000000000000000000000000000000_instance-2-1"

  The instance suffix is necessary to uniquely identify instances of
  abstract nodes rendered inside repeating dynamic contexts, like
  Collection Lists, Search or various Commerce contexts. Abstract nodes
  that are not dynamically repeated, do not need an instance suffix.

  The instance suffix consists of two parts: the prefix that identifies
  the string as an instance suffix and the instance index suffix.

  In this file, anything named `instanceSuffix` refers to the entire suffix,
  for example "_instance-2-5", and anything named `instanceIndexSuffix` refers
  to just the part that contains instance indices, for example "2-5".

  Example Instance Index Suffix: "2-1"

  The instance index suffix represents a path of instance indices from the
  root of the node tree to the current node. Each instance index on
  the path corresponds to the instance index generated for an abstract node
  rendered in a dynamic repeating context along that path. For example, an
  instance index suffix "2-1" means that the current node is within two
  dynamic repeating contexts, being the third instance of first repeating
  context and the second instance of the second repeating context (0-based
  indexing).

  When the instance index suffix is converted to an array of integers, we
  call the result `instanceIndexPath` in this file.

  Example Instance Index Path: [2, 1]

  Any variables named `instanceIndex` refer to the last element of the instance
  index path, i.e. the instance index corresponding to the closest repeating
  dynamic context of the current node.

  Example Instance Index: 1

  Outside this file, instance index paths are always stored and handled in
  the instance index suffix format. The reason is that string equality checking
  is faster and easier than array equality checking, which lends to more
  efficient rendering optimizations for Designer and Editor canvases, where
  the instance suffixes are used.
 */

export type NativeId = string;

type InstanceIndexPath = number[];

const SUFFIX_INDICES_SEPARATOR = '-';

const makeInstanceNativeId = (
  guid: string,
  instanceIndexSuffix: string
): NativeId => `${guid}_instance-${instanceIndexSuffix}`;

export const computeNativeId = (
  guid: string,
  environmentContext: EnvironmentContextType
): NativeId => {
  const instanceIndexSuffix = environmentContext.get('instanceIndexSuffix');

  if (typeof instanceIndexSuffix === 'string') {
    return makeInstanceNativeId(guid, instanceIndexSuffix);
  }

  return convertElementIdToNativeId(guid);
};

const formatInstanceIndexPathToSuffix = (
  instanceIndexPath: InstanceIndexPath
): string => instanceIndexPath.join(SUFFIX_INDICES_SEPARATOR);

const parseInstanceIndexPathFromSuffix = (
  instanceIndexSuffix: string
): InstanceIndexPath =>
  instanceIndexSuffix.split(SUFFIX_INDICES_SEPARATOR).map(Number);

export const getInstanceIndicesFromNativeId = (
  elementId: string
): InstanceIndexPath => {
  const instanceIndexSuffix = elementId.split('_instance-')[1];
  return instanceIndexSuffix
    ? parseInstanceIndexPathFromSuffix(instanceIndexSuffix)
    : [];
};

export const __DEPRECATED__getNativeId = (
  {
    element,
    environmentContext,
    isComponentRootElement,
    nativeIdPath,
    seenComponents,
  }: {
    element: ExpressionElement;
    environmentContext: EnvironmentContextType;
    isComponentRootElement: boolean;
    nativeIdPath: Array<string>;
    seenComponents: SeenComponents;
  },
  previewMode: boolean,
  runtimeEnvironment: Environment
) => {
  if (
    // When working with symbol/component root elements...
    isComponentRootElement &&
    // ...and when we're in the Designer, but *not* inside preview mode...
    ((runtimeEnvironment === WebflowEnvironments.DESIGNER && !previewMode) ||
      // ...OR when we know we're working within a Plugin Component:
      lastSeenComponentKind(seenComponents) === 'PluginComponent')
  ) {
    // ...we use the instance ID of the symbol/component we're inside rather
    // than the internal root element ID.
    const nativeId = nativeIdPath[nativeIdPath.length - 1];
    if (
      nativeId != null &&
      // ...we make an exception if we're currently focused on the symbol we're
      // inside; if that's the case we want to use the root ID as usual so it
      // can be interacted with like normal.
      nativeId !== last(environmentContext.get('focusedSymbolNativeIdPath'))
    ) {
      return nativeId;
    }
  }

  return computeNativeId(element.id, environmentContext);
};

export const getNativeIdWithElementId = (
  environmentContext: EnvironmentContextType,
  elementId: string
) => computeNativeId(elementId, environmentContext);

/**
 * Generates instance id for a node in a dynamic repeating context
 * @param {String}          guid              - The guid of the node.
 * @param {Number|Number[]} instanceIndexPath - The instance index.
 *
 * @returns {String} guid with appended instance suffix
 */
export const generateInstanceNativeId = (
  guid: string,
  instanceIndexPath: number | InstanceIndexPath
): NativeId => {
  if (typeof instanceIndexPath === 'number') {
    instanceIndexPath = [instanceIndexPath];
  }
  return makeInstanceNativeId(
    guid,
    formatInstanceIndexPathToSuffix(instanceIndexPath)
  );
};

/**
 * Returns the node id without the instance suffix.
 * @param {String} elementId - node native id that maybe has an instance suffix
 *
 * @returns {String} node id without instance suffix
 */
export const getIdWithoutInstance = (elementId: string): string =>
  getGuidNullable(String(elementId)) || '';

export const convertElementIdToNativeId: (elementId: string) => NativeId =
  identity;

/**
 * Returns the instance index from the instance index suffix of a native id.
 *
 * Example: getInstanceIndexFromSuffix("0-1") returns 1
 *
 * @param {String} instanceIndexSuffix - an instance index suffix
 *
 * @returns {Number} instance index of the instance index suffix
 */
const getInstanceIndexFromSuffix = (
  instanceIndexSuffix?: string | null
): number | null => {
  if (!instanceIndexSuffix) {
    return null;
  }
  const instanceIndex = last(
    parseInstanceIndexPathFromSuffix(instanceIndexSuffix)
  );
  // @ts-expect-error - TS2322 - Type 'number | null | undefined' is not assignable to type 'number | null'. | TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
  return isNaN(instanceIndex) ? null : instanceIndex;
};

export const getInstanceIndex = (
  environmentContext: EnvironmentContextType
): number | null =>
  getInstanceIndexFromSuffix(environmentContext.get('instanceIndexSuffix'));

/**
 * Returns the instance index from the instance suffix of a native id.
 *
 * Example: getInstanceIndexFromNativeId("nativeid_instance-0-1") returns 1
 *
 * @param {String} elementId - node native id that maybe has an instance suffix
 *
 * @returns {Number} instance index of the instance suffix
 */
export const getInstanceIndexFromNativeId = (
  elementId: string
): number | null =>
  getInstanceIndexFromSuffix(elementId.split('_instance-')[1]);

const appendInstanceIndex =
  (index: number) => (instanceIndexSuffix?: string | null) =>
    typeof instanceIndexSuffix === 'string'
      ? `${instanceIndexSuffix}${SUFFIX_INDICES_SEPARATOR}${index}`
      : String(index);

// cap cache depth at 100, max number of CMS items visible in a collection list
const instanceIndexMemo = cacheMemo(100);

export const updateInstanceIndexSuffix = weakMemo(
  (
    environmentContext: EnvironmentContextType
  ): ((arg1: number) => EnvironmentContextType) =>
    instanceIndexMemo(
      (index: number): EnvironmentContextType =>
        environmentContext.update(
          'instanceIndexSuffix',
          appendInstanceIndex(index)
        )
    )
);

type DissectedId = {
  guid: string | null;
  instanceSuffix: string | null;
};

/**
 * Returns an object {guid, instanceSuffix} that contains the DomNode guid and the instance suffix given a DOM data-w-id attribute
 * @param  {String}  id   The DOM `data-w-id` attribute we want to dissect
 * @return {Object}       An object containing {guid, instanceSuffix}
 */
export const dissectNativeId = (nativeId?: string | null): DissectedId => {
  if (!nativeId) {
    return {guid: null, instanceSuffix: null};
  }
  // Check if it's an instance (for dynlists items), and extract the instance suffix from the nativeId in that case
  const index = nativeId.indexOf('_instance');
  return index !== -1
    ? {guid: nativeId.slice(0, index), instanceSuffix: nativeId.slice(index)}
    : {guid: nativeId, instanceSuffix: null};
};

export const getGuidWithFullNativeId = (nativeId: NativeId) => {
  const {guid, instanceSuffix} = dissectNativeId(nativeId);

  return {
    guid,
    fullNativeId:
      guid && instanceSuffix ? `${guid}${instanceSuffix}` : nativeId,
  };
};

export const getGuidNullable = (
  nativeId: string | null | undefined
): string | null => {
  return dissectNativeId(nativeId).guid;
};

type SafeDissectedId = {
  guid: string;
  instanceSuffix: string | null;
};

function dissectNativeIdSafely(nativeId: NativeId): SafeDissectedId {
  // Check if it's an instance (for dynlists items), and extract the instance suffix from the nativeId in that case
  const index = nativeId.indexOf('_instance');
  return index !== -1
    ? {guid: nativeId.slice(0, index), instanceSuffix: nativeId.slice(index)}
    : {guid: nativeId, instanceSuffix: null};
}

export function getGuid(nativeId: NativeId): string {
  return dissectNativeIdSafely(nativeId).guid;
}

export const getInstanceSuffix = (
  nativeId: string | null | undefined
): string => dissectNativeId(nativeId).instanceSuffix || '';

export const copyInstanceSuffix =
  (nativeId: string | null) => (elementId: string) =>
    elementId + getInstanceSuffix(nativeId);

const WFID_ATTR = SharedConfig.WEBFLOW_NODE_ID_ATTR;
const WF_DATA_ID_ATTR = SharedConfig.WEBFLOW_NODE_ID_PATH_ATTR;
const WF_INSTANCEID_ATTR = SharedConfig.webflowNodeInstanceIdAttr;
const CMS_COLLECTION_ID_ATTR = SharedConfig.CMS_COLLECTION_ID_ATTR;
const WF_FRAME_ID_ATTRIBUTE = 'data-wf-frame-id';

/**
 * Find and return a native node by its native id. It works even if the native
 * DOM node lacks the instance suffix on the `data-w-id` attribute. @param  {String}     nativeId  The native id
 * @return {DOMElement}           The DOM element (if any)
 */
export const getNativeNodeByNativeId = (
  nativeId?: string | null,
  frameId?: string | null
): HTMLElement | null => {
  if (!nativeId) {
    return null;
  }

  const iframe = getActiveIframe();

  const doc = iframe?.contentDocument;

  if (!doc) return null;

  if (nativeId === '__HTML__') {
    return doc.documentElement;
  }

  // if frameId is provided, it means we're in the Open Canvas
  // this allows us to query the correct frame for the node instead of all frames in the document
  const root = frameId
    ? doc.querySelector<HTMLElement>(`[${WF_FRAME_ID_ATTRIBUTE}="${frameId}"]`)
    : doc;

  if (!root) {
    return null;
  }

  const queryString = defineNativeIdQueryString(nativeId);

  const exactMatch = root.querySelector<HTMLElement>(queryString);

  if (exactMatch) {
    return exactMatch;
  }

  const id = getIdWithoutInstance(nativeId);
  const index = getInstanceIndexFromNativeId(nativeId);

  if (!id || index == null) {
    return null;
  }

  return (
    root.querySelectorAll<HTMLElement>(defineNativeIdQueryString(id))[index] ||
    null
  );
};

export const getNativeNodeByNativeIdPath = (
  nativeIdPath: string[],
  frameId?: string | null
): HTMLElement | null => {
  const doc = getActiveDocument();
  if (!doc) {
    return null;
  }

  if (nativeIdPath.length === 0 && nativeIdPath[0] === '__HTML__') {
    return doc.documentElement;
  }

  const root = frameId
    ? doc.querySelector<HTMLElement>(`[${WF_FRAME_ID_ATTRIBUTE}="${frameId}"]`)
    : doc;
  if (!root) {
    return null;
  }

  const queryString = defineNativeIdPathQueryString(nativeIdPath);
  return root.querySelector(queryString) || null;
};

/**
 * Waits until the Code-Island's element with a matching
 * `data-shadow-wf-id` appears inside the shadow-DOM.
 */
const waitForCodeIslandReady = (
  shadowRoot: ShadowRoot,
  nativeIdPath: string[]
): Promise<HTMLElement | null> => {
  const query = `[data-shadow-wf-id='${JSON.stringify(nativeIdPath)}']`;

  const immediate = shadowRoot.querySelector<HTMLElement>(query);
  if (immediate) {
    return Promise.resolve(immediate);
  }

  return new Promise<HTMLElement | null>((resolve) => {
    let settled = false;
    const finish = (el: HTMLElement | null) => {
      if (!settled) {
        settled = true;
        observer.disconnect();
        clearTimeout(timer);
        resolve(el);
      }
    };

    const observer = new MutationObserver(() => {
      const el = shadowRoot.querySelector<HTMLElement>(query);
      if (el) {
        finish(el);
      }
    });

    // Observe for both DOM changes AND attribute changes
    // The data-shadow-wf-id attribute is set on an existing element after component renders
    observer.observe(shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-shadow-wf-id'],
    });

    const timer = setTimeout(() => finish(null), 500);
  });
};

/**
 * Async version that waits for CodeIsland shadow DOM to be ready
 */
export const getMeasurableNativeNodeByNativeIdPathAsync = async (
  nativeIdPath: string[],
  frameId?: string | null
): Promise<HTMLElement | null> => {
  const nativeNode = getNativeNodeByNativeIdPath(nativeIdPath, frameId);

  if (!nativeNode) {
    return null;
  }

  if (shouldUseCodeIslandHostForMeasurement(nativeNode)) {
    return nativeNode;
  }

  const maybeShadowRoot = nativeNode.shadowRoot;
  if (!maybeShadowRoot) {
    return nativeNode;
  }

  // If the node is a code island, wait for the shadow root element
  const shadowNode = await waitForCodeIslandReady(
    maybeShadowRoot,
    nativeIdPath
  );

  return shadowNode ?? null;
};

export type GetMeasurableNativeNodeByNativeOptions = {
  resolveInShadowRoot: (shadowRoot: ShadowRoot) => HTMLElement | null;
};

export const getMeasurableNativeNodeByNativeIdPath = (
  nativeIdPath: string[],
  frameId?: string | null,
  options?: GetMeasurableNativeNodeByNativeOptions
): HTMLElement | null => {
  const nativeNode = getNativeNodeByNativeIdPath(nativeIdPath, frameId);
  if (!nativeNode) {
    return null;
  }

  const maybeShadowRoot = nativeNode.shadowRoot;

  // If the node is a code island, find the shadow root element
  // We expect the shadow root element to have a `data-shadow-wf-id` attribute
  // that matches the native id path
  if (maybeShadowRoot) {
    if (options?.resolveInShadowRoot) {
      const shadowNode = options.resolveInShadowRoot(maybeShadowRoot);
      if (shadowNode) return shadowNode;
      // Fall through to default shadow root resolution below;
    }

    // Default shadow root node resolution
    const queryString = `[data-shadow-wf-id='${JSON.stringify(nativeIdPath)}']`;
    const shadowNode = maybeShadowRoot.querySelector(queryString);
    return shadowNode as HTMLElement;
  }

  // Always fallback to the host element
  return nativeNode;
};

/**
 * Calls `onChange` with the new measurable node whenever `initialNode`, the one
 * resolved for `nativeIdPath`, is replaced after the fact, and returns the
 * unsubscribe.
 *
 * A code island swaps its measurable node on its own, with no Flux change to
 * signal it: the loading placeholder gives way to the component's root, and a
 * reload rebuilds the whole host. Watching the host's shadow root catches the
 * former, and the host's parent the latter.
 *
 * Only code islands are observed. For any other node, light-DOM ones included,
 * this is a no-op: if such a node is replaced, the caller's `ResizeObserver`
 * stays on the detached node (see EXT-2850).
 */
export const observeMeasurableNativeNodeChanges = (
  initialNode: HTMLElement,
  nativeIdPath: string[],
  frameId: string | null | undefined,
  options: GetMeasurableNativeNodeByNativeOptions | undefined,
  onChange: (node: HTMLElement) => void
): (() => void) => {
  let node = initialNode;
  const root = node.getRootNode();
  if (!('host' in root)) return () => undefined;

  const swapObserver = new MutationObserver(() => {
    if (node.isConnected) return;

    watchHost(getNativeNodeByNativeIdPath(nativeIdPath, frameId));

    const next = getMeasurableNativeNodeByNativeIdPath(
      nativeIdPath,
      frameId,
      options
    );
    // Not rendered yet: the host's shadow root is watched again above, so its
    // `data-shadow-wf-id` appearing re-triggers this.
    if (!next || next === node) return;

    node = next;
    onChange(next);
  });

  const watchHost = (host: Element | null) => {
    // Nothing new to watch yet: keep the current observation so the
    // replacement host's insertion still re-triggers the callback.
    if (!host?.shadowRoot) return;

    swapObserver.disconnect();
    swapObserver.observe(host.shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-shadow-wf-id'],
    });
    if (host.parentNode) {
      swapObserver.observe(host.parentNode, {childList: true});
    }
  };
  watchHost((root as ShadowRoot).host);

  return () => swapObserver.disconnect();
};

/**
 * Find and return all native nodes given an element id
 */
export const getAllNativeNodesByElementId = (
  elementId: string
): Array<HTMLElement> => {
  const doc = getActiveDocument();
  if (!doc) {
    return [];
  }

  if (elementId === '__HTML__') {
    return [doc.documentElement];
  }
  return elementId
    ? Array.from<HTMLElement>(
        doc.querySelectorAll(
          `[${WFID_ATTR}="${elementId}"], [${WFID_ATTR}^="${elementId}_instance"]`
        )
      )
    : [];
};

export const getNativeNodeByInstanceId = (instanceId: string) => {
  const doc = getActiveDocument();
  if (!doc) {
    return null;
  }

  const queryString = defineInstanceNativeIdPathQueryString(instanceId);

  return doc.querySelector(queryString) || null;
};

/**
 * Returns whether a nativeId corresponds to a static items in a collection list
 */
export const isStaticNodeInCollectionList = (nativeId: NativeId): boolean => {
  if (!nativeId.includes('_instance-')) {
    // if nativeId does not have the _instance- suffix, it is not in a collection list
    return false;
  } else {
    const nativeNode = getNativeNodeByNativeId(nativeId);
    const collectionId =
      nativeNode?.getAttribute(CMS_COLLECTION_ID_ATTR) || null;
    if (collectionId) {
      // if the node in the collection list has a collection id, it is not static
      return false;
    } else {
      return true;
    }
  }
};

/**
 * Find and return all native nodes with an instance suffix matching the provided native id
 */
export const getAllNativeNodeInstancesByNativeId = (nativeId: NativeId) => {
  const doc = getActiveDocument();
  if (!doc) {
    return [];
  }

  return Array.from<HTMLElement>(
    doc.querySelectorAll(
      `[${WFID_ATTR}^="${getIdWithoutInstance(nativeId)}_instance"]`
    )
  );
};

/**
 * Finds all native nodes wrapped in optimization nodes for the variation provided.
 */
export const getAllNativeNodesInCurrentVariation = (variationId: string) => {
  const nodes: Array<HTMLElement> = [];
  const doc = getActiveDocument();
  if (!doc) {
    return [];
  }
  // CSS selector for all the DIVs with that data attribute that are not descendants of a .w-hidden element.
  doc
    .querySelectorAll(
      `div[data-intellimize-var-${variationId}=""]:not(.w-hidden *)`
    )
    .forEach((node) => {
      const child = node.firstElementChild;
      if (child) {
        nodes.push(child as HTMLElement);
      }
    });
  return nodes;
};

/**
 * Build a query string to find the native node depending on its instance native id
 */
const defineInstanceNativeIdPathQueryString = (nativeId: string) =>
  `[${WF_INSTANCEID_ATTR}="${nativeId}"]`;

/**
 * Build a query string to find the native node depending on its native id
 */
const defineNativeIdQueryString = (nativeId: string) =>
  `[${WFID_ATTR}="${nativeId}"]`;

/**
 * Build a query string to find the native node depending on its native id path
 */
export const defineNativeIdPathQueryString = (nativeIdPath: Array<string>) =>
  `[${WF_DATA_ID_ATTR}='${JSON.stringify(nativeIdPath)}']`;

// Needed to gently help Flow produce a `NativeId[]` from a manually written `string[]`
export const toNativeIdPath = (arr: string[]): NativeId[] =>
  arr.map(convertElementIdToNativeId);

/**
 * Validates that the given `nativeIdPath` is an array containing only strings.
 */
export const isValidNativeIdPath = (path: NativeId[]) => {
  return Array.isArray(path) && path.every((item) => typeof item === 'string');
};
